import React, { useEffect, useState } from "react";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Autocomplete, Button, Popover, Stack, TextField, TextFieldProps } from "@mui/material";

import { IClientFilters, ClientType } from "store/types/user-management";

import "./Filters.styles.scss";

const commonInputProps: {
  sx?: TextFieldProps["sx"];
  size: TextFieldProps["size"];
  InputLabelProps: TextFieldProps["InputLabelProps"];
} = {
  size: "small",
  InputLabelProps: { shrink: true }
};

const CLIENT_TYPES: { label: string; value: ClientType }[] = [
  { label: "Individual", value: "Individual" },
  { label: "Family", value: "Family" },
  { label: "Legal entity", value: "Legal entity" }
];

interface IFiltersProps {
  queries: IClientFilters;
  onChange: (partial: Partial<IClientFilters>) => void;
  isFilterOpen: boolean;
  onFilterToggle: (open: boolean) => void;
  rmOptions: string[];
}

export const Filters: React.FC<IFiltersProps> = ({ queries, onChange, isFilterOpen, onFilterToggle, rmOptions }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Local popover state seeded from the latest queries prop.
  const [selectedType, setSelectedType] = useState<ClientType | null>(queries.type ? (queries.type as ClientType) : null);
  const [selectedRm, setSelectedRm] = useState<string | null>(queries.rm ? (queries.rm as string) : null);

  // Fix #3: Sync local popover state whenever the parent's queries change
  // (e.g. after a create, filter, or search).  This prevents the popover
  // from showing stale values that do not reflect the currently active
  // filter state.
  useEffect(() => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
  }, [queries.type, queries.rm]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onFilterToggle(true);
  };

  // Fix #6: Reset local state whenever the popover closes so that a
  // subsequent open always reflects the current active filters rather
  // than whatever the user previously selected but didn't apply.
  const handleClose = () => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
    setAnchorEl(null);
    onFilterToggle(false);
  };

  const handleApply = () => {
    onChange({
      type: selectedType || "",
      rm: selectedRm || ""
    });
    handleClose();
  };

  const handleCancel = () => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
    handleClose();
  };

  // Transform flat string list into Autocomplete-compatible objects.
  const rmOptionsObjects = rmOptions
    .filter((name): name is string => typeof name === "string" && name.trim().length > 0)
    .map((name) => ({ label: name.trim(), value: name.trim() }));

  const isFiltersSelected = Boolean(selectedType || selectedRm);

  return (
    <div className="clients-filters">
      <Button
        className="clients-filters__btn"
        onClick={handleClick}
        variant="outlined"
        size="sm"
        startIcon={<FilterAltIcon />}
        sx={{ backgroundColor: isFiltersSelected ? "#dae0e2" : "transparent" }}
      >
        Filters
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div className="clients-filters__popup">
          <Autocomplete
            options={CLIENT_TYPES}
            value={selectedType}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <li {...props}>{option.label}</li>
            )}
            onChange={(_, val) => setSelectedType(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                {...commonInputProps}
                label="Type"
                placeholder={selectedType || "Select"}
              />
            )}
          />

          {/* Fix #2: Use real RM options from the API instead of hardcoded values. */}
          <Autocomplete
            options={rmOptionsObjects}
            value={selectedRm}
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option) => (
              <li {...props}>{option}</li>
            )}
            onChange={(_, val) => setSelectedRm(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                {...commonInputProps}
                label="RM"
                placeholder={selectedRm || "Select"}
              />
            )}
          />

          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="contained"
              sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Stack>
        </div>
      </Popover>
    </div>
  );
};
